import { ASSIGNMENT_KIND_MAP } from 'services/assignments';
import {
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  FileTextOutlined,
  LoadingOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import { CardItem } from 'components/professor_area/CardItem';
import { MenuInfo } from 'rc-menu/lib/interface';
import { Modal, Space, Tag, Tooltip, Typography } from 'antd';
import { formatDate } from 'services/date';
import { getAPI } from 'requests/api';
import { useCallback, useMemo } from 'react';
import { useCorporateReportExport } from 'pages/corporate_reports/hooks/useCorporateReportExport';
import { useNavigation, useNotification } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import type { IProfessorAreaEvaluationsList } from 'interfaces/professor_area/evaluations';
import type { MenuProps } from 'antd/lib';

interface AssignmentItemProps {
  item: IProfessorAreaEvaluationsList;
  onRemove: (evaluationId: string) => void;
  onAddReplacement: (assignment: IProfessorAreaEvaluationsList) => void;
  onAssociateStudents: (assignment: IProfessorAreaEvaluationsList) => void;
  canShow: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCreate: boolean;
  canAssociateStudents: boolean;
  canDownloadSubmissions: boolean;
}

export const TabAssignmentListItem = ({
  item,
  onRemove,
  onAddReplacement,
  onAssociateStudents,
  canShow,
  canEdit,
  canDelete,
  canCreate,
  canAssociateStudents,
  canDownloadSubmissions
}: AssignmentItemProps) => {
  const navigate = useNavigation();
  const notification = useNotification();
  const params = useParams();
  const classroomId = params.id;

  const { editUrl } = navigate;

  const urlEdit = useMemo(() => {
    return editUrl(`professor_area/classroom_assignments`, item.id, {
      classroomId
    });
  }, [classroomId, editUrl, item.id]);

  const canHaveReplacement = item.kind === 'regular' || item.kind === 'final';

  // Only a "substitutiva especial" (rule=classroom_enrollment) is restricted to
  // hand-picked students, so that's the only case that can associate students.
  const canAssociateReplacementStudents =
    item.kind === 'replacement' && item.rule === 'classroom_enrollment';

  const { reportId, handleExportReport } = useCorporateReportExport({
    storageKey: `bulk_download_attempts:${item.id}`,
    searchFormProps: {},
    notification,
    createReport: async () => {
      const response = await getAPI().post(
        `/admin/professor_area/classrooms/${classroomId}/evaluations/${item.id}/bulk_download`
      );

      return { id: response.data.id };
    }
  });

  const isDownloading = Boolean(reportId);

  const handleConfirmDownload = useCallback(() => {
    const count = item.realized_attempts_count ?? 0;
    const countText = count === 1 ? '1 entrega' : `${count} entregas`;

    Modal.confirm({
      title: 'Baixar entregas da atividade',
      icon: <DownloadOutlined />,
      content: `Você vai baixar ${countText}. A geração do arquivo pode levar alguns minutos — você pode fechar esta página, o download começará automaticamente quando estiver pronto.`,
      okText: 'Confirmar download',
      cancelText: 'Cancelar',
      onOk: () => handleExportReport()
    });
  }, [handleExportReport, item.realized_attempts_count]);

  const items = useMemo<MenuProps['items']>(() => {
    const base: MenuProps['items'] = [];

    if (canShow) {
      base.push({
        key: 'show',
        label: 'Corrigir',
        icon: <EyeOutlined />
      });
    }

    // Bulk download of submissions. Gated on the same permission the backend
    // policy (bulk_download?) checks — classroom_assignments/attempts:list, the
    // permission that lists the submissions being exported.
    if (canDownloadSubmissions) {
      base.push({
        key: 'download',
        label: (
          <Tooltip
            title={
              item.submission_period_ended
                ? ''
                : 'Disponível após o encerramento do prazo de entrega'
            }
          >
            {/* pointerEvents:auto so the tooltip still shows while the item is disabled */}
            <span style={{ pointerEvents: 'auto' }}>Baixar entregas</span>
          </Tooltip>
        ),
        icon: isDownloading ? <LoadingOutlined /> : <DownloadOutlined />,
        disabled: !item.submission_period_ended || isDownloading
      });
    }

    if (canEdit) {
      base.push({
        key: 'edit',
        label: 'Editar atividade',
        icon: <EditOutlined />
      });
    }

    if (canHaveReplacement && canCreate) {
      base.push({
        key: 'replacement',
        label: 'Adicionar substitutiva',
        icon: <FileAddOutlined />
      });
    }

    if (canAssociateReplacementStudents && canAssociateStudents) {
      base.push({
        key: 'associate',
        label: 'Associar alunos',
        icon: <UsergroupAddOutlined />
      });
    }

    if (canDelete) {
      base.push({
        key: 'delete',
        label: 'Excluir atividade',
        icon: <DeleteOutlined />
      });
    }

    return base;
  }, [
    canAssociateReplacementStudents,
    canAssociateStudents,
    canCreate,
    canDelete,
    canEdit,
    canHaveReplacement,
    canShow,
    canDownloadSubmissions,
    isDownloading,
    item.submission_period_ended
  ]);

  const handleMenuClick = useCallback(
    (event: MenuInfo) => {
      switch (event.key) {
        case 'show': {
          navigate.push(`${urlEdit}?tab=attempts`);
          break;
        }

        case 'download': {
          handleConfirmDownload();
          break;
        }

        case 'edit': {
          navigate.edit(
            `professor_area/classroom_assignments`,
            item.id,
            'push',
            {
              classroomId
            }
          );

          break;
        }

        case 'replacement': {
          onAddReplacement(item);
          break;
        }

        case 'associate': {
          onAssociateStudents(item);
          break;
        }

        case 'delete': {
          onRemove(item.id);
          break;
        }

        default: {
          break;
        }
      }
    },
    [
      classroomId,
      handleConfirmDownload,
      item,
      navigate,
      onAddReplacement,
      onAssociateStudents,
      onRemove,
      urlEdit
    ]
  );

  const menuProps: MenuProps = {
    items,
    onClick: handleMenuClick
  };

  const evaluationDate = useMemo(() => {
    const rules = item.requirements?.[0]?.rules ?? [];

    const starts_at =
      rules.find(rule => rule.operator === 'after')?.value || '';
    const ends_at = rules.find(rule => rule.operator === 'before')?.value || '';

    return {
      starts_at,
      ends_at
    };
  }, [item.requirements]);

  return (
    <CardItem
      icon={FileTextOutlined}
      title={item.name}
      menuProps={menuProps}
      meta={
        <>
          <Typography.Text>{ASSIGNMENT_KIND_MAP[item.kind]}</Typography.Text>

          {item.kind === 'regular' && (
            <Typography.Text>Peso {item.weight}</Typography.Text>
          )}

          <Space size="small">
            <CalendarOutlined />
            <Typography.Text>
              {formatDate(evaluationDate.starts_at, 'DD/MM/YYYY')}
              {' > '}
              {formatDate(evaluationDate.ends_at, 'DD/MM/YYYY')}
            </Typography.Text>
          </Space>
        </>
      }
      extraContent={
        <Tag
          color={
            (item.pending_evaluation_attempts_count ?? 0) > 0
              ? 'orange'
              : 'success'
          }
        >
          {item.pending_evaluation_attempts_count ?? '0'} atividades a corrigir
        </Tag>
      }
    />
  );
};
